import { useEffect } from 'react';

import {
  BackgroundContainer,
  OneColumnExpanded,
  HeaderContainer,
  HeroContainerRow,
  Image,
} from './styled';
import { useQuery } from '@tanstack/react-query';
import { queryKey, getCareers } from './queries';
import DocumentHeader from '@components/DocumentHeader';
import { SWS_DESCRIPTION_HOME } from '@/constants';
import { Navigation } from '@components/Navigation';
import { Hero } from './components/Hero/Hero';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import { WorkWithUs } from './components/WorkWithUs/WorkWithUs';
import { Benefits } from './components/Benefits/Benefits';
import { Positions } from './components/Positions/Positions';
import { Gallery } from './components/Gallery/Gallery';
import { Values } from './components/Values/Values';
import { TeamReviews } from './components/TeamReviews/TeamReviews';
import { Journey } from './components/Journey/Journey';
import { Location } from './components/Location/Location';
import Footer from '@components/Footer';
import { useContentfulImage } from '@/hooks/useContentfulImage';

const LoadingOverlay = loadable(() => import('@components/LoadingOverlay'));

const Careers = () => {
  const { data, status } = useQuery({
    queryKey,
    queryFn: getCareers,
    staleTime: Infinity,
  });
  const { trackEvent } = useTracking();

  const heroImage = useContentfulImage({
    url:
      typeof data !== 'undefined' &&
      typeof data['careers-hero'].galleryCollection !== 'undefined'
        ? data['careers-hero'].galleryCollection.items[0].url
        : undefined,
    title: 'Sydney Harbor',
    format: 'avif',
    breakpoints: [
      { width: 2000, resolution: '2x' },
      { width: 1200, resolution: '1x' },
      { width: 1023, resolution: '3x' },
      { width: 768, resolution: '2x' },
      { width: 480, resolution: '1x' },
    ],
  });
  const heroImageFallback = useContentfulImage({
    url:
      typeof data !== 'undefined' &&
      typeof data['careers-hero'].galleryCollection !== 'undefined'
        ? data['careers-hero'].galleryCollection.items[0].url
        : undefined,
    title: 'Sydney Harbor',
    breakpoints: [
      { width: 2000, resolution: '2x' },
      { width: 1200, resolution: '1x' },
      { width: 1023, resolution: '3x' },
      { width: 768, resolution: '2x' },
      { width: 480, resolution: '1x' },
    ],
  });

  useEffect(() => {
    trackEvent({
      type: 'track',
      page: 'careers',
      subject: 'page',
      action: 'landed',
    });
  }, [trackEvent]);

  if (status === 'pending' || typeof data === 'undefined')
    return <LoadingOverlay />;

  return (
    <>
      <BackgroundContainer>
        <HeroContainerRow justifyContent="center">
          {heroImage && heroImageFallback && (
            <picture>
              <source srcSet={heroImage.srcSet} type="image/avif" />
              <Image
                srcSet={heroImageFallback.srcSet}
                src={heroImageFallback.src}
                alt={heroImageFallback.alt}
                width="2000"
                height="670"
              />
            </picture>
          )}
          <OneColumnExpanded>
            <HeaderContainer>
              <DocumentHeader
                allowCrawlers
                title="Careers"
                description={SWS_DESCRIPTION_HOME}
              >
                {heroImage && (
                  <link rel="preload" as="image" href={heroImage.src} />
                )}
                {heroImageFallback && (
                  <link rel="preload" as="image" href={heroImageFallback.src} />
                )}
                <link rel="canonical" href="https://simplywall.st/careers" />
                <script type="text/javascript">{`window.leverJobsOptions = {accountName: 'simplywallst', includeCss: true};`}</script>
                <script
                  type="text/javascript"
                  src="https://andreasmb.github.io/lever-jobs-embed/index.js"
                ></script>
              </DocumentHeader>
              <Navigation />
            </HeaderContainer>
            <Hero
              title={data['careers-hero'].title}
              shortContentOne={data['careers-hero'].shortContentOne}
            />
          </OneColumnExpanded>
        </HeroContainerRow>

        <Positions
          title="Open Positions"
          subtitle="Sydney headquarter with hybrid & remote work options"
        />
        <WorkWithUs
          title={data['careers-workWithUs'].title}
          markdownContent={[
            data['careers-workWithUs'].longContentOne,
            data['careers-workWithUs'].longContentTwo,
          ]}
          gallery={data['careers-workWithUs'].galleryCollection?.items}
        />
        <Benefits
          title={data['careers-benefits'].title}
          subtitle={data['careers-benefits'].subtitle}
          markdownContent={[
            data['careers-benefits'].longContentOne,
            data['careers-benefits'].longContentTwo,
            data['careers-benefits'].longContentThree,
          ]}
          gallery={data['careers-benefits'].galleryCollection?.items}
        />
        <Gallery gallery={data.gallery} mobileGallery={data.mobileGallery} />
        <Values
          title={data['careers-values'].title}
          subtitle={data['careers-values'].subtitle}
          markdownContent={[
            data['careers-values'].longContentOne,
            data['careers-values'].longContentTwo,
            data['careers-values'].longContentThree,
            data['careers-values'].longContentFour,
            data['careers-values'].longContentFive,
          ]}
        />
        <TeamReviews
          title={data['careers-teamReviews'].title}
          markdownContent={[
            data['careers-teamReviews'].longContentOne,
            data['careers-teamReviews'].longContentTwo,
            data['careers-teamReviews'].longContentThree,
          ]}
          gallery={data['careers-teamReviews'].galleryCollection?.items}
        />
        <Journey
          title={data['careers-journey'].title}
          markdownContent={[
            data['careers-journey'].longContentOne,
            data['careers-journey'].longContentTwo,
            data['careers-journey'].longContentThree,
            data['careers-journey'].longContentFour,
          ]}
          shortContentOne={data['careers-journey'].shortContentOne}
          shortContentTwo={data['careers-journey'].shortContentTwo}
          shortContentThree={data['careers-journey'].shortContentThree}
          shortContentFour={data['careers-journey'].shortContentFour}
        />
        <Location
          title={data['careers-location'].title}
          subtitle={data['careers-location'].subtitle}
          markdownContent={[data['careers-location'].longContentOne || '']}
        />
      </BackgroundContainer>
      <OneColumnExpanded>
        <Footer />
      </OneColumnExpanded>
    </>
  );
};

export default Careers;
